import { useDefaultDataLayer } from '../useDataLayer';

/**
 * Hook for tracking buttons/links interactions
 * @param {Object} options - Configuration options
 * @param {string} options.componentName - Name of the component containing the button/link
 * @param {string} options.location - Location on the page (header, body, footer, overlay)
 * @returns {Object} - Tracking methods
 */
export function useButtonsAndLinksTracking(options = {}) {
  const { componentName = '', location = 'body' } = options;
  const { registerInteraction } = useDefaultDataLayer();

  /**
   * Track button/link state change
   * @param {string} linkText - The text of the button/link
   * @param {string} intentText - The intent text for the interaction
   * @param {Object} additionalData - Any additional data to include
   * @param {string} event - The event type ('click_button' or 'click_link')
   */
  const trackState = (linkText, intentText = null, additionalData = {}, event = 'click_button') => {
    const link_text = linkText?.toLowerCase() || '';
    const intent = intentText || link_text.replace(/\s+/g, '_') || '';

    registerInteraction({
      event,
      link_text,
      component: componentName,
      intent,
      event_location: location,
      ...additionalData,
    });
  };

  /**
   * Track button state change
   * @param {string} buttonText - The text of the button
   * @param {string} intent - The intent text for the interaction
   * @param {Object} additionalData - Any additional data to include
   */
  const trackButtonState = (buttonText, intent = null, additionalData = {}) => {
    trackState(buttonText, intent, { ...additionalData }, 'click_button');
  };

  /**
   * Track link state change
   * @param {string} linkText - The text of the link
   * @param {string} intent - The intent text for the interaction
   * @param {Object} additionalData - Any additional data to include
   */
  const trackLinkState = (linkText, intent = null, additionalData = {}) => {
    trackState(linkText, intent, { ...additionalData }, 'click_link');
  };

  return {
    trackState,
    trackButtonState,
    trackLinkState,
  };
}
